import { MakeLogicType, actions, afterMount, connect, kea, listeners, path, props, selectors } from 'kea'
import posthog from 'posthog-js'

import { DataNodeLogicProps, dataNodeLogic } from '~/queries/nodes/DataNode/dataNodeLogic'
import { ErrorTrackingIssue, ErrorTrackingQuery } from '~/queries/schema/schema-general'

import type {
    DataNode,
    DateRange,
    ErrorTrackingIssueAssignee,
    ErrorTrackingIssueStatus,
    ErrorTrackingQueryResponse,
    EventsQueryResponse,
    HogQLAutocompleteResponse,
    HogQLMetadataResponse,
    HogQLQueryResponse,
    HogQueryResponse,
    LogAttributesQueryResponse,
    LogValuesQueryResponse,
    LogsQueryResponse,
    MetricsQueryResponse,
    RefreshType,
    SessionsQueryResponse,
    TraceSpansAggregationQueryResponse,
    TraceSpansAttributeBreakdownQueryResponse,
    TraceSpansQueryResponse,
    TraceSpansTreeQueryResponse,
} from '../../../../frontend/src/queries/schema/schema-general'
import { issueActionsLogic } from '../components/IssueActions/issueActionsLogic'
import { mergeIssues } from '../utils'
import { batchSpikeEventsLogic } from './batchSpikeEventsLogic'

const ISSUE_STATE_MUTATION_NAMES = new Set([
    'resolveIssues',
    'suppressIssues',
    'activateIssues',
    'assignIssues',
    'updateIssueAssignee',
    'updateIssueStatus',
    'updateIssueSeverity',
    'updateIssueName',
    'updateIssueDescription',
])

export interface IssuesDataNodeLogicProps {
    query: DataNodeLogicProps['query']
    key: DataNodeLogicProps['key']
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface issuesDataNodeLogicValues {
    needsReload: boolean // issueActionsLogic
    response:
        | ErrorTrackingQueryResponse
        | HogQLAutocompleteResponse
        | HogQLMetadataResponse
        | HogQLQueryResponse<any[]>
        | HogQueryResponse
        | LogAttributesQueryResponse
        | LogValuesQueryResponse
        | MetricsQueryResponse
        | Record<string, any>
        | SessionsQueryResponse
        | TraceSpansAggregationQueryResponse
        | TraceSpansAttributeBreakdownQueryResponse
        | TraceSpansQueryResponse
        | null // nodeLogic
    responseLoading: boolean // nodeLogic
    results: ErrorTrackingIssue[]
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface issuesDataNodeLogicActions {
    loadSpikeEventsForIssues: (
        issueIds: string[],
        dateRange?: DateRange | undefined
    ) => {
        dateRange: DateRange | undefined
        issueIds: string[]
    } // batchSpikeEventsLogic
    activateIssues: (ids: string[]) => {
        ids: string[]
    } // issueActionsLogic
    assignIssues: (
        ids: string[],
        assignee: ErrorTrackingIssueAssignee | null
    ) => {
        assignee: ErrorTrackingIssueAssignee | null
        ids: string[]
    } // issueActionsLogic
    clearNeedsReload: () => {
        value: true
    } // issueActionsLogic
    mergeIssues: (ids: string[]) => {
        ids: string[]
    } // issueActionsLogic
    mutationFailure: (
        mutationName: string,
        error: unknown
    ) => {
        error: unknown
        mutationName: string
    } // issueActionsLogic
    mutationSuccess: (mutationName: string) => {
        mutationName: string
    } // issueActionsLogic
    resolveIssues: (ids: string[]) => {
        ids: string[]
    } // issueActionsLogic
    suppressIssues: (ids: string[]) => {
        ids: string[]
    } // issueActionsLogic
    updateIssueAssignee: (
        id: string,
        assignee: ErrorTrackingIssueAssignee | null
    ) => {
        assignee: ErrorTrackingIssueAssignee | null
        id: string
    } // issueActionsLogic
    updateIssueSeverity: (
        id: string,
        severity: null | import('~/queries/schema').ErrorTrackingQueryIssueSeverity
    ) => {
        id: string
        severity: null | import('~/queries/schema').ErrorTrackingQueryIssueSeverity
    } // issueActionsLogic
    updateIssueStatus: (
        id: string,
        status: ErrorTrackingIssueStatus
    ) => {
        id: string
        status: ErrorTrackingIssueStatus
    } // issueActionsLogic
    cancelQuery: () => {
        value: true
    } // nodeLogic
    loadData: (
        refresh?: RefreshType | undefined,
        alreadyRunningQueryId?: string | undefined,
        overrideQuery?: DataNode<Record<string, any>> | undefined
    ) => {
        overrideQuery: DataNode<Record<string, any>> | undefined
        pollOnly: boolean
        queryId: string
        refresh: RefreshType | undefined
    } // nodeLogic
    loadDataFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    } // nodeLogic
    loadDataSuccess: (
        response:
            | ErrorTrackingQueryResponse
            | HogQLAutocompleteResponse
            | HogQLMetadataResponse
            | HogQLQueryResponse<any[]>
            | HogQueryResponse
            | LogAttributesQueryResponse
            | LogValuesQueryResponse
            | MetricsQueryResponse
            | Record<string, any>
            | SessionsQueryResponse
            | TraceSpansAggregationQueryResponse
            | TraceSpansAttributeBreakdownQueryResponse
            | TraceSpansQueryResponse
            | null
            | undefined,
        payload?:
            | {
                  overrideQuery: DataNode<Record<string, any>> | undefined
                  pollOnly: boolean
                  queryId: string
                  refresh: RefreshType | undefined
              }
            | undefined
    ) => {
        payload?: {
            overrideQuery: DataNode<Record<string, any>> | undefined
            pollOnly: boolean
            queryId: string
            refresh: RefreshType | undefined
        }
        response:
            | ErrorTrackingQueryResponse
            | HogQLAutocompleteResponse
            | HogQLMetadataResponse
            | HogQLQueryResponse<any[]>
            | HogQueryResponse
            | LogAttributesQueryResponse
            | LogValuesQueryResponse
            | MetricsQueryResponse
            | Record<string, any>
            | SessionsQueryResponse
            | TraceSpansAggregationQueryResponse
            | TraceSpansAttributeBreakdownQueryResponse
            | TraceSpansQueryResponse
            | null
            | undefined
    } // nodeLogic
    setResponse: (
        response:
            | ErrorTrackingQueryResponse
            | EventsQueryResponse
            | HogQLAutocompleteResponse
            | HogQLMetadataResponse
            | HogQLQueryResponse<any[]>
            | HogQueryResponse
            | LogAttributesQueryResponse
            | LogsQueryResponse
            | LogValuesQueryResponse
            | MetricsQueryResponse
            | Record<string, any>
            | SessionsQueryResponse
            | TraceSpansAggregationQueryResponse
            | TraceSpansAttributeBreakdownQueryResponse
            | TraceSpansQueryResponse
            | TraceSpansTreeQueryResponse
    ) =>
        | ErrorTrackingQueryResponse
        | EventsQueryResponse
        | HogQLAutocompleteResponse
        | HogQLMetadataResponse
        | HogQLQueryResponse<any[]>
        | HogQueryResponse
        | LogAttributesQueryResponse
        | LogsQueryResponse
        | LogValuesQueryResponse
        | MetricsQueryResponse
        | Record<string, any>
        | SessionsQueryResponse
        | TraceSpansAggregationQueryResponse
        | TraceSpansAttributeBreakdownQueryResponse
        | TraceSpansQueryResponse
        | TraceSpansTreeQueryResponse // nodeLogic
    reloadData: () => {}
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface issuesDataNodeLogicMeta {
    __keaTypeGenInternalSelectorTypes: {
        results: (
            response:
                | ErrorTrackingQueryResponse
                | HogQLAutocompleteResponse
                | HogQLMetadataResponse
                | HogQLQueryResponse<any[]>
                | HogQueryResponse
                | LogAttributesQueryResponse
                | LogValuesQueryResponse
                | MetricsQueryResponse
                | Record<string, any>
                | SessionsQueryResponse
                | TraceSpansAggregationQueryResponse
                | TraceSpansAttributeBreakdownQueryResponse
                | TraceSpansQueryResponse
                | null
        ) => ErrorTrackingIssue[]
    }
}

export type issuesDataNodeLogicType = MakeLogicType<
    issuesDataNodeLogicValues,
    issuesDataNodeLogicActions,
    IssuesDataNodeLogicProps,
    issuesDataNodeLogicMeta
>

export const issuesDataNodeLogic = kea<issuesDataNodeLogicType>([
    path(['products', 'error_tracking', 'logics', 'issuesDataNodeLogic']),
    props({} as IssuesDataNodeLogicProps),

    connect(({ key, query }: IssuesDataNodeLogicProps) => {
        const nodeLogic = dataNodeLogic({ key, query, refresh: 'blocking' })
        return {
            values: [nodeLogic, ['response', 'responseLoading'], issueActionsLogic, ['needsReload']],
            actions: [
                nodeLogic,
                ['setResponse', 'loadData', 'loadDataSuccess', 'loadDataFailure', 'cancelQuery'],
                batchSpikeEventsLogic,
                ['loadSpikeEventsForIssues'],
                issueActionsLogic,
                [
                    'mergeIssues',
                    'resolveIssues',
                    'suppressIssues',
                    'activateIssues',
                    'assignIssues',
                    'updateIssueAssignee',
                    'updateIssueStatus',
                    'updateIssueSeverity',
                    'mutationSuccess',
                    'mutationFailure',
                    'clearNeedsReload',
                ],
            ],
        }
    }),

    actions({
        reloadData: () => ({}),
    }),

    selectors({
        results: [
            (s) => [s.response],
            (
                response:
                    | Record<string, any>
                    | null
                    | import('~/queries/schema').ErrorTrackingQueryResponse
                    | import('~/queries/schema').HogQLAutocompleteResponse
                    | import('~/queries/schema').HogQLMetadataResponse
                    | import('~/queries/schema').HogQLQueryResponse<any[]>
                    | import('~/queries/schema').HogQueryResponse
                    | import('~/queries/schema').LogAttributesQueryResponse
                    | import('~/queries/schema').LogValuesQueryResponse
                    | import('~/queries/schema').MetricsQueryResponse
                    | import('~/queries/schema').SessionsQueryResponse
                    | import('~/queries/schema').TraceSpansAggregationQueryResponse
                    | import('~/queries/schema').TraceSpansAttributeBreakdownQueryResponse
                    | import('~/queries/schema').TraceSpansQueryResponse
            ): ErrorTrackingIssue[] => (response && 'results' in response ? response.results : []),
        ],
    }),

    listeners(({ values, actions, props, cache }) => ({
        loadData: () => {
            cache.loadStartTime = performance.now()
        },
        reloadData: () => {
            actions.loadData('force_blocking')
        },
        loadDataSuccess: () => {
            const durationMs = cache.loadStartTime != null ? Math.round(performance.now() - cache.loadStartTime) : null

            const response = values.response as Record<string, any> | null
            const results = response && 'results' in response ? response.results : []
            const query = props.query as Record<string, any>
            const filterGroups = query?.filterGroup?.values ?? []
            const filterCount = filterGroups.reduce(
                (count: number, group: any) => count + (group?.values?.length ?? 0),
                0
            )
            const sortBy = query?.orderBy ?? null
            const sortDirection = query?.orderDirection ?? null
            posthog.capture('error_tracking_issue_list_loaded', {
                duration_ms: durationMs,
                result_count: (results as ErrorTrackingIssue[]).length,
                is_cached: response?.is_cached ?? null,
                filter_count: filterCount,
                sort_by: sortBy,
                sort_direction: sortDirection,
                assignee_filter: !!query?.assignee,
                status_filter: query?.status ?? null,
            })

            const issueIds = (results as ErrorTrackingIssue[]).map((issue) => issue.id).filter(Boolean)
            if (issueIds.length > 0) {
                const dateRange = (props.query as ErrorTrackingQuery).dateRange
                actions.loadSpikeEventsForIssues(issueIds, dateRange)
            }
        },
        // optimistically update local results
        mergeIssues: ({ ids }) => {
            const { results } = values

            const [primaryId, ...sourceIds] = ids
            const primaryIssue = results.find(({ id }) => id === primaryId)
            const sourceIssues = results.filter(({ id }) => sourceIds.includes(id))

            if (primaryIssue && sourceIssues.length > 0) {
                const mergedIssue = mergeIssues(primaryIssue, sourceIssues)

                actions.setResponse({
                    ...values.response,
                    results: results
                        .filter(({ id }) => !sourceIds.includes(id))
                        .map((issue) => (issue.id === primaryIssue.id ? mergedIssue : issue)),
                })
            }
        },
        resolveIssues: ({ ids }) => {
            const { results } = values
            actions.setResponse({
                ...values.response,
                results: results.map((issue) => {
                    if (ids.includes(issue.id)) {
                        return { ...issue, status: 'resolved' }
                    }
                    return issue
                }),
            })
        },
        suppressIssues: ({ ids }) => {
            const { results } = values
            actions.setResponse({
                ...values.response,
                results: results.map((issue) => {
                    if (ids.includes(issue.id)) {
                        return { ...issue, status: 'suppressed' }
                    }
                    return issue
                }),
            })
        },
        activateIssues: ({ ids }) => {
            const { results } = values
            actions.setResponse({
                ...values.response,
                results: results.map((issue) => {
                    if (ids.includes(issue.id)) {
                        return { ...issue, status: 'active' }
                    }
                    return issue
                }),
            })
        },

        assignIssues: ({ ids, assignee }) => {
            const { results } = values
            actions.setResponse({
                ...values.response,
                results: results.map((issue) =>
                    // replace primary issue
                    ids.includes(issue.id) ? { ...issue, assignee } : issue
                ),
            })
        },

        updateIssueAssignee: ({ id, assignee }) => {
            const response = values.response
            if (response) {
                const results = ('results' in response ? response.results : []) as ErrorTrackingIssue[]
                if (results.some((issue) => issue.id === id)) {
                    actions.setResponse({
                        ...response,
                        results: results.map((issue) => (issue.id === id ? { ...issue, assignee } : issue)),
                    })
                }
            }
        },

        updateIssueStatus: ({ id, status }) => {
            const response = values.response
            if (response) {
                const results = ('results' in response ? response.results : []) as ErrorTrackingIssue[]
                if (results.some((issue) => issue.id === id)) {
                    actions.setResponse({
                        ...response,
                        results: results.map((issue) => (issue.id === id ? { ...issue, status } : issue)),
                    })
                }
            }
        },

        updateIssueSeverity: ({ id, severity }) => {
            const response = values.response
            if (response) {
                const results = ('results' in response ? response.results : []) as ErrorTrackingIssue[]
                if (results.some((issue) => issue.id === id)) {
                    actions.setResponse({
                        ...response,
                        results: results.map((issue) => (issue.id === id ? { ...issue, severity } : issue)),
                    })
                }
            }
        },

        mutationSuccess: ({ mutationName }) => {
            if (ISSUE_STATE_MUTATION_NAMES.has(mutationName)) {
                actions.reloadData()
            }
        },
        mutationFailure: () => actions.reloadData(),
    })),

    afterMount(({ values, actions, cache }) => {
        cache.loadStartTime = performance.now()
        if (values.needsReload) {
            actions.clearNeedsReload()
            actions.reloadData()
        }
    }),
])
